import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { ModalActionsComponent, PasslyButton, PasslyTextField } from '@app/components';
import { onPressEnterModal, genericErrorString } from '@app/helpers';
import { getLoaderBtn, getModalSwitch, setExceptionHandler } from '@app/store/actions';
import { FormControl, Link } from '@material-ui/core';
import { workflowRequestService, basicService } from '@app/services';
import { ISingleWorkflowRequest, ICreateRequestApplication } from '@app/models';

export interface IReceivedData {
    id: string;
    handleRefresh: Function;
}

const ValidateWorkflowDialog = (props: IReceivedData) => {
    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [buttonState, setButtonState] = useState(false);
    const [showLaunch, setShowLaunch] = useState(false);
    const [showVerification, setShowVerification] = useState(false);
    const [showCreationOption, setShowCreationOption] = useState(false);
    const [workflowSteps, setWorkflowSteps] = useState([]);
    const [workflowValues, setWorkflowValues] = useState([] as any);
    const [app, setApp] = useState<ISingleWorkflowRequest>({} as ISingleWorkflowRequest);
    const [testUrl, setTestUrl] = useState('');
    const [testId, setTestId] = useState('');

    // -------------------------------------------
    // ----------------- EFFECTS -----------------
    // -------------------------------------------
    useEffect(() => {
        dispatch(setExceptionHandler(true));

        workflowRequestService.workflowCreationRequests(props.id, handleError, handleGetWorkflowSuccess);
    }, []);

    const handleError = (error: any) => {
        setErrorMessage(genericErrorString(error));

        dispatch(getLoaderBtn(false));
    };

    // example workflow
    // [{"step":"Navigate","type":"Navigation","getelementby":null,"elementval":null,"value":"https://gravityzone.bitdefender.com/"},{"step":"input","type":"text","getelementby":"Name","elementval":"email","value":"Username","delay":7000,"getframeby":"","frameval":""},{"step":"input","type":"password","getelementby":"Name","elementval":"password","value":"Password","delay":7000,"getframeby":"","frameval":""},{"step":"submitform","type":"button","getelementby":"ElementName","elementval":"login","value":null,"delay":7000,"getframeby":"","frameval":"","formval":"form","getformby":"ElementName"}]
    const handleGetWorkflowSuccess = (response: any) => {
        const results: ISingleWorkflowRequest = response.data;

        if (results) {
            setApp(results);
            setWorkflowSteps(JSON.parse(results.Workflow));
        }
    };

    const handleCancel = () => {
        if (testId !== '') {
            deleteTestApp();
        }
        else {
            dispatch(getModalSwitch(false));
        }
    };

    const handleSuccess = () => {
        dispatch(getLoaderBtn(false));
        dispatch(getModalSwitch(false));
        props.handleRefresh(true);
    };

    const handleValidateBtn = () => {
        setButtonState(true);
        dispatch(getLoaderBtn(true));

        let values: any = {};

        for (let i: number = 0; i < workflowValues.length; i++) {
            values[workflowValues[i].key] = workflowValues[i].value;
        }

        let jsonObject = {
            WorkflowValues: JSON.stringify(values),
            ReqId: app.Id,
            Test: true,
            AppName: '',
            AppCompany: '',
            AppIconUrl: ''
        };

        workflowRequestService.createRequestApplication(jsonObject, handleError, handleCreateTestSuccess);
    };

    const handleCreateTestSuccess = (response: any) => {
        const results: ICreateRequestApplication = response.data;

        if (results) {
            setTestUrl('/trust/launch?WorkflowId=' + results.Id);
            setTestId(results.Id);
            setShowLaunch(true);
            setShowVerification(false);
            setShowCreationOption(false);
            setButtonState(false);
            dispatch(getLoaderBtn(false));
        }
    };

    const isInputStep = (step: any) => {
        if (step.step && step.step === 'input') {
            return true;
        }
        return false;
    };

    const workflowAttrLabelValue = (rawVal: string) => {
        if (rawVal) {
            if (rawVal.toLowerCase() === 'password') {
                return translation('WORKFLOW_PLACEHOLDER_PASSWORD');
            }
            else if (rawVal.toLowerCase() === 'username') {
                return translation('WORKFLOW_PLACEHOLDER_USERNAME');
            }
        }

        return rawVal;
    };

    const onStepValueChange = (e: any) => {
        const value: string = e.target.value;
        const key: string = e.target.name;

        if (key && value) {
            let index = -1;
            for (let i: number = 0; i < workflowValues.length; i++) {
                if (workflowValues[i].key === key) {
                    index = i;
                    break;
                }
            }

            if (index > -1) {
                workflowValues.splice(index, 1);
            }

            workflowValues.push({ 'key': key, 'value': value });

            setWorkflowValues(workflowValues);
        }
    };

    const launchTest = () => {
        setShowLaunch(false);
        setShowVerification(true);
    };

    const testPassed = () => {
        workflowRequestService.deleteTestApplication(testId, handleError);

        setTestId('');
        setTestUrl('');
        setShowVerification(false);
        setShowCreationOption(true);
        setButtonState(true);
    };

    const handleCreateApplication = () => {
        app.State = 'Validated';

        workflowRequestService.updateWorkflowRequest(app.Id, app, handleError, handleUpdatedWorkflowFromPassedSuccess);
    };

    const handleUpdatedWorkflowFromPassedSuccess = () => {
        let jsonObject = {
            WorkflowValues: '',
            ReqId: app.Id,
            Test: false,
            AppName: '',
            AppCompany: '',
            AppIconUrl: window.location.origin + '/assets/images/apps/app-large.png'
        };

        workflowRequestService.createRequestApplication(jsonObject, handleError, handleCreateAppSuccess);
    };

    const handleCreateAppSuccess = (response: any) => {
        const results: ICreateRequestApplication = response.data;

        basicService.redirect('/sso/library/details/' + results.Id);
    };

    const testFailed = () => {
        app.State = 'Failed';

        workflowRequestService.updateWorkflowRequest(app.Id, app, handleError, handleUpdatedWorkflowFromFailedSuccess);
    };

    const handleUpdatedWorkflowFromFailedSuccess = () => {
        deleteTestApp();
    };

    const deleteTestApp = () => {
        if (testId !== '') {
            workflowRequestService.deleteTestApplication(testId, handleError, handleSuccess);
        }
    };

    return (
        <>
        <div className='modal__form' tabIndex={0} onKeyPress={onPressEnterModal(handleValidateBtn, buttonState)}>
            <div className='modal__large'>
                <div className='modal__body--row'>
                    {translation('TEST_WORKFLOW_BODY')}
                </div>
                {workflowSteps.length > 0 &&
                    <>
                        <div className='modal__body--row'>
                            <br />
                            <h4>{translation('WORKFLOW_ATTRIBUTES')}</h4>
                        </div>

                        <form className='modal__form' method='post'>
                            {workflowSteps.map((item: any, index: number) => (
                                <React.Fragment key={index}>
                                    {isInputStep(item) &&
                                        <FormControl>
                                            <PasslyTextField
                                                fullWidth
                                                id={item.elementval}
                                                label={workflowAttrLabelValue(item.value)}
                                                name={item.elementval}
                                                type={item.type}
                                                onChange={onStepValueChange}
                                            />
                                        </FormControl>
                                    }
                                </React.Fragment>
                            ))}
                        </form>
                    </>
                }

                {showLaunch &&
                    <>
                        <div className='workflowRequest__validateButtons'>
                            <Link href={testUrl} id='launchTest' onClick={launchTest} target='_blank'>
                                {translation('LAUNCH_TEST')}
                            </Link>
                        </div>
                    </>
                }

                {!showVerification &&
                    <>
                        <div className='workflowRequest__validateButtons'>
                            <div className='workflowRequest__validateHeader'>{translation('TEST_WORKFLOW_VERIFICATION_HEADER')}</div>

                            <PasslyButton
                                className='modal__body__field--button'
                                variant='contained'
                                color='primary'
                                onClick={testPassed}>
                                {translation('TEST_WORKFLOW_VERIFICATION_PASSED')}
                            </PasslyButton>
                            &nbsp;
                            <PasslyButton
                                className='modal__body__field--button'
                                variant='contained'
                                color='primary'
                                onClick={testFailed}>
                                {translation('TEST_WORKFLOW_VERIFICATION_FAILED')}
                            </PasslyButton>
                        </div>
                    </>
                }

                {showCreationOption &&
                    <>
                        <div className='workflowRequest__validateButtons'>
                            <PasslyButton
                                className='modal__body__field--button'
                                variant='contained'
                                color='primary'
                                onClick={handleCreateApplication}>
                                {translation('VALID_CREATE_APP')}
                            </PasslyButton>
                        </div>
                    </>
                }
            </div>

            {
                errorMessage &&
                <div className='modal__error'>{errorMessage}</div>
            }
        </div>
            <ModalActionsComponent
                primaryBtn={translation('TEST_BUTTON')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleValidateBtn}
                onClickSecondary={handleCancel}
                disabled={buttonState}
                id='validateWorkflow'
            />
        </>
    );
};

export default ValidateWorkflowDialog;
